/* Small line icons, drawn inline so they take the text colour and need no
 * icon font. One stroke width, one size: they sit next to text, not instead
 * of it, except in the row actions where the title says what they do. */

type IconName =
  | 'open'
  | 'edit'
  | 'trash'
  | 'download'
  | 'agentic'
  | 'manual'
  | 'dashboard'
  | 'log'
  | 'settings'
  | 'collapse'
  | 'expand'
  | 'table'
  | 'chart'
  | 'check'
  | 'chevron'
  | 'close'

const PATHS: Record<IconName, string> = {
  close: 'M6 6l12 12 M18 6L6 18',
  open: 'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  edit: 'M4 20h4L19 9l-4-4L4 16v4Z M13.5 6.5l4 4',
  trash: 'M4 7h16 M9 7V4h6v3 M6 7l1 13h10l1-13 M10 11v6 M14 11v6',
  download: 'M12 4v11 M7 10l5 5 5-5 M5 20h14',
  // Agentic: a spark - the agent decides.
  agentic: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3Z M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z',
  // Manual: sliders - you set the rule.
  manual: 'M4 6h9 M17 6h3 M4 12h3 M11 12h9 M4 18h11 M19 18h1 M15 4v4 M9 10v4 M17 16v4',
  dashboard: 'M4 4h7v7H4z M13 4h7v4h-7z M13 10h7v10h-7z M4 13h7v7H4z',
  log: 'M6 3h9l4 4v14H6z M14 3v5h5 M9 12h7 M9 16h7',
  settings:
    'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z',
  collapse: 'M4 4h16v16H4z M9 4v16 M15 9l-3 3 3 3',
  expand: 'M4 4h16v16H4z M9 4v16 M13 9l3 3-3 3',
  table: 'M4 5h16v14H4z M4 10h16 M4 15h16 M10 5v14',
  chart: 'M4 19h16 M5 15l4-5 4 3 6-7',
  check: 'M5 12l5 5 9-10',
  chevron: 'M6 9l6 6 6-6',
}

export function Icon({ name, size = 16, title }: { name: IconName; size?: number; title?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      style={{ flex: 'none' }}
    >
      {title && <title>{title}</title>}
      <path d={PATHS[name]} />
    </svg>
  )
}

/* A square button holding one icon; the title is its name. */
export function IconButton({
  icon,
  title,
  onClick,
  danger,
  disabled,
}: {
  icon: IconName
  title: string
  onClick: () => void
  danger?: boolean
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      className={`icon-button ${danger ? 'danger' : ''}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <Icon name={icon} />
    </button>
  )
}

/* Applied = the family is shown with this simulation (or this plan). It
   follows the choice: ticking another simulation moves it at once. */
export function Applied({ on }: { on: boolean }) {
  return on ? (
    <span className="chip good"><span aria-hidden="true">●</span>applied</span>
  ) : (
    <span className="chip neutral">not applied</span>
  )
}

/* Agentic or manual, with its icon - the same everywhere a run is listed. */
export function ModeChip({ mode }: { mode: string }) {
  const agentic = mode === 'auto'
  return (
    <span className={`chip mode ${agentic ? 'agentic' : 'manual'}`}>
      <Icon name={agentic ? 'agentic' : 'manual'} size={13} />
      {agentic ? 'Agentic' : 'Manual'}
    </span>
  )
}
